import { ColumnSort, PaginationState } from '@tanstack/react-table'
import React, { useMemo, useState } from 'react'
import { mapReleaseDate } from '../../../../graphql/schema/queries/helpers/library'
//  import { BulkActionBar } from '../../../../grid/bulk-action-bar'
import { LibraryAgentLanguage } from '../library/library-agent-language'
import {
  AccessComplexity,
  AccessVector,
  AuthenticationRequired,
  AvailabilityImpact,
  ConfidentialityImpact,
  IntegrityImpact,
  Vulnerability
} from '../../../../components/shared'
import { LibraryGridPanelID } from '../../helpers/drilldown-ui-interfaces'
import { LibraryDrillDownPanel } from '../../components/drilldown-panel'
import { Cvss2Vectors, parseCvssVectors } from '../../helpers/cvssUtils'
import { CvssScoringType, Library } from '../../helpers/ui-interfaces'
import {
  LibraryFilterType,
  LibrarySCA,
  VulnerabilityCLI
} from '../../helpers/libraries-interfaces'
import { cliFilters, useCLiLibraries } from '../../api/react-query'
import { getColumns } from './columns'
import { useFilters } from './useFilters'
import { useLibrariesPageContext } from '../../pages'
import { useIntl } from 'react-intl'
import { getGoLibraryName } from '../../helpers/helpers'
import { useCurrentOrganization } from 'contrastBase/state'
import { Layout } from '../filters/layout'
import { useFilterDefinitions } from '../filters/use-filter-definitions'
import { Table } from '@contrast-security-inc/design-system-tables'
import { css } from '@emotion/css'
import { useDeepCompareEffect } from 'use-deep-compare'
import { QuickFilter } from '../filters/types'
// import { useRowSelection } from '../hooks/useRowSelection'
// import { RowSelectionActionList } from './action-list'

export interface CliGridProps {
  organizationID: string
  testId?: string
}

export const queryParamsToAPIQueryString = (
  queryParams: Record<string, string | string[]>
): string => {
  if (!queryParams) {
    return ''
  }

  const unsupportedFields = [
    'view',
    'activeRow',
    'showPanel',
    'filterText',
    'quickFilter' // quickFilter is already covered by the context
  ]

  return (
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      // Remove fields that the API does not support
      if (unsupportedFields.includes(key)) {
        return acc
      }

      const valueStr = Array.isArray(value) ? value.join(',') : value

      if (valueStr) {
        acc += `&${key.toLowerCase()}=${encodeURIComponent(
          valueStr.replaceAll(`${key}.`, '')
        )}`
      }

      return acc
    }, '') || ''
  )
}

export const generateAPIQueryParamFromFilterText = ({
  filterText
}: {
  filterText?: string
}) => {
  if (filterText) {
    const regex = /^cve-\d/

    const isCve = regex.test(filterText.toLowerCase())
    return `&${isCve ? 'cve' : 'name'}=${filterText.replaceAll('%', '')}`
  }
  return ''
}

export const mapVulnerabilityCLIToVulnerability = (
  vuln: VulnerabilityCLI
): Vulnerability => {
  let cvss2Vectors: Cvss2Vectors = {}
  if (vuln.cvss2Vector) {
    cvss2Vectors = parseCvssVectors(
      vuln.cvss2Vector,
      CvssScoringType.CVSS2
    ) as Cvss2Vectors
  }
  return {
    ...vuln,
    severityCode: vuln.severity,
    cvss3SeverityCode: vuln.cvss3Severity,
    visible: !!vuln.description,
    hasCve: !!(vuln.cvss3Vector || vuln.cvss2Vector),
    ...(vuln.cvss2Vector
      ? {
          authentication: cvss2Vectors.authentication
            ?.value as AuthenticationRequired,
          accessVector: cvss2Vectors.accessVector?.value as AccessVector,
          accessComplexity: cvss2Vectors.accessComplexity
            ?.value as AccessComplexity,
          availabilityImpact: cvss2Vectors.availabilityImpact
            ?.value as AvailabilityImpact,
          confidentialityImpact: cvss2Vectors.confidentialityImpact
            ?.value as ConfidentialityImpact,
          integrityImpact: cvss2Vectors.integrityImpact
            ?.value as IntegrityImpact,
          references: []
        }
      : {})
  }
}

export const cliGridTestId = 'libraries-cli-grid'
export const quickFilterParamName = 'quickFilter'

export const CliGrid = ({
  organizationID,
  testId = cliGridTestId
}: CliGridProps) => {
  // const [rowSelection, setRowSelection] = useState({})
  const { formatMessage } = useIntl()
  const { applicationID, filterText, gridType, selectedQuickFilter } =
    useLibrariesPageContext()

  // Assess Grid and CLI grid do not share the same quick filters
  const validQuickFilter = ['ALL', 'VULNERABLE'].includes(selectedQuickFilter)
    ? selectedQuickFilter
    : 'ALL'
  const [sortingOrder, setSortingOrder] = useState<ColumnSort[]>([
    {
      id: 'severity',
      desc: true
    }
  ])
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [selectedPanelLibrary, setSelectedPanelLibrary] = useState<Library>()

  const [selectedAggregationFilters, setSelectedAggregationFilters] =
    useState<any>(null)

  const { currentOrganization: organization } = useCurrentOrganization()

  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10
    })

  const { libraryFilters, quickFilterOptions, results } = useFilters({
    organizationID,
    applicationID,
    searchParam: generateAPIQueryParamFromFilterText({
      filterText
    }),
    quickFilterParam: validQuickFilter,
    filterParams: queryParamsToAPIQueryString(selectedAggregationFilters)
  })

  const {
    data: cliLibraryData,
    isLoading: isLoadingLibraries,
    refetch: refetchLibraries,
    isFetching: isFetchingLibraries
  } = useCLiLibraries(
    `/Contrast/api/sca/organizations/${organizationID}${
      applicationID ? `/applications/${applicationID}` : ''
    }/libraries?quickFilter=${validQuickFilter}&page=${pageIndex}&size=${pageSize}${
      selectedAggregationFilters
        ? `${queryParamsToAPIQueryString(selectedAggregationFilters)}`
        : ''
    }${generateAPIQueryParamFromFilterText({
      filterText
    })}&sort=${sortingOrder[0].id.toLowerCase()},${
      sortingOrder[0].desc ? 'desc' : 'asc'
    }`,
    true
  )

  const quickFilters: QuickFilter[] = React.useMemo(() => {
    return (
      quickFilterOptions?.map(({ label, count }) => {
        return {
          name:
            label.toLowerCase() === 'all'
              ? formatMessage({ id: 'title_all_libraries' })
              : formatMessage({ id: 'title_vulnerable' }),
          count,
          id: label
        }
      }) || []
    )
  }, [quickFilterOptions, formatMessage])

  const selectedQuickFilterMap = React.useMemo(
    () => quickFilters.find(({ id }) => id === validQuickFilter),
    [quickFilters, validQuickFilter]
  )

  // The Panel should really be updated to handle either type of vulnerability
  const setPanelLibrary = (library: LibrarySCA) => {
    setIsPanelOpen(true)

    const vulns = !library.vulnerabilities
      ? []
      : library.vulnerabilities
          .map(mapVulnerabilityCLIToVulnerability)
          .sort((vulnerability1, vulnerability2) => {
            return (
              Math.max(
                vulnerability2.severityValue || 0,
                vulnerability2.cvss3SeverityValue || 0
              ) -
              Math.max(
                vulnerability1.severityValue || 0,
                vulnerability1.cvss3SeverityValue || 0
              )
            )
          })

    const name =
      library.libraryLanguage.toUpperCase() === 'GO'
        ? getGoLibraryName(library)
        : library.artifactName

    setSelectedPanelLibrary({
      ...library,
      name,
      hash: library.hash,
      language: LibraryAgentLanguage.fromValue('Java'),
      ossEnabled: false,
      tags: [],
      bugTrackers: [],
      versions: {
        latest: {
          version: library.latestVersion,
          releaseDate: mapReleaseDate(library.latestVersionReleaseDate)
        },
        current: {
          version: library.version,
          releaseDate: mapReleaseDate(library.releaseDate)
        }
      },
      policies: {
        invalidVersion: false,
        restrictedByOrg: false,
        restrictedLicense: false,
        versionRestriction: 0
      },
      licenses: library.licenses,
      id: library.hash,
      grade: { score: 20, value: 'F' },
      app_language: 'JAVA',
      applications: [],
      servers: [],
      mergedApplications: [],
      libraryClassUsageCounts: [],
      usage: { total: 0, totalUsed: 0 },
      vulnerabilities: vulns
    })
  }

  const columns = useMemo(() => {
    return organization ? getColumns({ organization, setPanelLibrary }) : []
  }, [organization])

  const isLoading = isLoadingLibraries || isFetchingLibraries

  const { filterState } = useFilterDefinitions({
    libraryFilters,
    excludeFilters: [LibraryFilterType.QuickFilter],
    results,
    initialFilters: cliFilters
  })

  useDeepCompareEffect(() => {
    // Reset page index when filtering
    setPagination((prevState) => ({
      ...prevState,
      pageIndex: 0
    }))
  }, [selectedQuickFilter, filterState.filters])

  React.useEffect(() => {
    setSelectedAggregationFilters(filterState.filters)
  }, [filterState.filters, libraryFilters])

  return (
    <Layout
      filterState={filterState}
      quickFilters={quickFilters}
      selectedQuickFilterMap={selectedQuickFilterMap}
    >
      <Table<LibrarySCA>
        className={css`
          padding: 0 24px 24px 24px;
          /* Removes space on top of the header showing when scrolling. */
          thead > tr {
            top: -1px;
          }
        `}
        columns={columns}
        data={
          cliLibraryData && cliLibraryData.content ? cliLibraryData.content : []
        }
        isLoading={isLoading}
        PaginationProps={{
          allowFirstPageNav: true,
          totalResults: cliLibraryData?.totalElements,
          showInputNav: true
        }}
        tableOptions={{
          enableSorting: true,
          manualPagination: true,
          onSortingChange: (updaterOrValue) => setSortingOrder(updaterOrValue),
          onPaginationChange: (getPagination: () => PaginationState) => {
            const { pageIndex, pageSize } = getPagination() || {}
            if (pageSize) {
              setPagination({
                pageIndex,
                pageSize
              })
            }
          },
          pageCount: cliLibraryData?.totalPages,
          state: { sorting: sortingOrder, pagination: { pageIndex, pageSize } },
          initialState: { sorting: sortingOrder }
        }}
        testId={testId}
        // BulkSelectionProps={{
        //   renderBulkSelectionItems: () =>
        //     gridType === 'CLI' && applicationID ? (
        //       <RowSelectionActionList
        //         orgId={organizationID}
        //         applicationID={applicationID}
        //         libraryIds={
        //           table.getIsAllPageRowsSelected() || selectedCount > pageSize
        //             ? []
        //             : table
        //                 .getSelectedRowModel()
        //                 .rows.map((lib) => lib.original.uuid)
        //         }
        //         excludedLibraries={
        //           table.getIsAllPageRowsSelected() || selectedCount < pageSize
        //             ? []
        //             : excludedRows.map((lib) => lib.original.uuid)
        //         }
        //       />
        //     ) : null
        // }}
      />
      {isPanelOpen && organization && selectedPanelLibrary && (
        <LibraryDrillDownPanel
          ossEnabled={organization.ossLicense}
          organizationID={organizationID}
          onClose={() => {
            setIsPanelOpen(false)
            setSelectedPanelLibrary(undefined)
          }}
          selectedPanelTabId={LibraryGridPanelID.Overview}
          applicationID={applicationID}
          gridType={gridType}
          organization={organization}
          library={selectedPanelLibrary}
          refresh={refetchLibraries}
        />
      )}
    </Layout>
  )
}
